import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { api, apiJson } from "@/api/client.js";
import { HumourPanel, MealsPanel, NotesPanel, OrganizationPanel, PrepPhotoPanel, TaskBoard, isLiveTask, openCount } from "@/components/CockpitPanels.jsx";
import { GangDesk } from "@/components/GangDesk.jsx";
import { DayTeams } from "@/components/DayTeams.jsx";
import { CloseDay } from "@/components/HuntDay.jsx";
import { HuntConditions } from "@/components/HuntConditions.jsx";
import { DaySheet } from "@/components/DaySheet.jsx";
import { dayLabel } from "@/lib/dayLabels.js";
import { GroupMap } from "@/components/GroupMap.jsx";
import { HunterCockpit } from "@/components/HunterCockpit.jsx";
import { RouteModal } from "@/components/RouteModal.jsx";
import { GearFacts, Readiness, RoomsPanel, SepaqInscription, VehicleFacts, WeatherCard } from "@/components/HuntMorning.jsx";
import { DutyList, HarvestPanel, ObservationForm, OfficialSources, PermitReminder, PersonPack, ReadyStrip, ReturnPanel, TerritoryGuide } from "@/components/CompanionPanels.jsx";
import { LUNCH } from "@/content/huntBoard.js";
import { HUNT_SUPPERS } from "@/content/huntMeals.js";
import { SEPAQ_PDF } from "@/content/sepaqHunt2026.js";
import { NordQuestCenter } from "@/components/NordQuestCenter.jsx";
import { ExpensesPage } from "@/pages/ExpensesPage.jsx";
import { PhotoGallery } from "@/components/PhotoGallery.jsx";
import { GroupBoard } from "@/components/GroupBoard.jsx";
import { PWSWorkModal } from "@/components/PWSWorkModal.jsx";
import { useAuth } from "@/context/AuthContext.jsx";
import { useMarcelMode } from "@/context/MarcelMode.jsx";
import { AdminCockpit } from "@/components/AdminCockpit.jsx";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";
import { companionPhase, isoDate, phaseDate } from "@/lib/huntDates.js";
import { defaultDestination, nextStep, torontoIso } from "@/lib/nowPlan.js";

const TerritoryMap = lazy(() => import("@/components/TerritoryMap.jsx").then((mod) => ({ default: mod.TerritoryMap })));

const HUNTER_ORDER = ["mem_luc", "mem_eric", "mem_marcel", "mem_pierre", "mem_steve", "mem_dan"];

export function TodayPage() {
  const { ui } = useOrignalUi();
  const { user } = useAuth();
  const { isMarcel, mode, actingAsAdmin } = useMarcelMode();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);
  const [layer, setLayer] = useState(null);
  const layerRef = useRef(null);
  layerRef.current = layer;
  const [pdfOpen, setPdfOpen] = useState(false);
  const [mapBits, setMapBits] = useState(null);
  const [live, setLive] = useState(() => new Date());
  const [sheetDay, setSheetDay] = useState("");
  const [weatherSnap, setWeatherSnap] = useState(null);

  useEffect(() => {
    api("/api/bootstrap").then(setData).catch((err) => setError(err.message));
    api("/api/tasks").then((body) => setTasks(body.items || [])).catch(() => setTasks([]));
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setLive(new Date()), 30000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    setModal(null);
    setLayer(null);
  }, [mode]);

  useEffect(() => {
    if (modal !== "map" && modal !== "territory") return undefined;
    let stop = false;
    Promise.all([
      api("/api/territory/points").catch(() => ({ items: [] })),
      api("/api/territory/secteur4").catch(() => null),
    ]).then(([points, limit]) => {
      if (!stop) setMapBits({ points: points.items || [], limit });
    });
    return () => { stop = true; };
  }, [modal]);

  if (error) {
    return <main className="og-cockpit23" data-testid="page-today"><p className="og-error">{ui.errors.network}</p></main>;
  }
  if (!data) {
    return <main className="og-cockpit23" data-testid="page-today"><p className="og-hint">{ui.app.loading}</p></main>;
  }

  const hunt = data.hunt || {};
  const t = ui.hunt;
  const w = t.work;
  const prospect = hunt.prospection || {};
  const terr = hunt.territory || {};
  const after = hunt.after_hunt || {};
  const bilan = prospect.bilan || { label: prospect.expense_label || t.bilanPending };
  const hunters = (hunt.hunters || []).slice().sort((a, b) => HUNTER_ORDER.indexOf(a.id) - HUNTER_ORDER.indexOf(b.id));
  const present = prospect.present || [];
  const photos = (hunt.photos || []).filter((photo) => photo.thumb_url || photo.url).slice(0, 3);
  const now = live;
  const phase = companionPhase();
  const todayIso = phase === "hunt" ? isoDate() : phaseDate(phase);
  const activeDay = sheetDay || (torontoIso(now) < "2026-10-02" ? "2026-10-02" : torontoIso(now) > "2026-10-10" ? "2026-10-10" : torontoIso(now));
  const admin = actingAsAdmin;
  const meId = user?.member_id || user?.member?.id;
  const lodgingName = data.lodging?.name || prospect.lodge || "Chalet Desrochers";
  const unknown = t.unknown;

  function hunterLabel(hunter) {
    const count = openCount(tasks, hunter.id);
    return count > 0 ? w.todoN.replace("{n}", String(count)) : w.allDone;
  }

  async function toggleTask(task) {
    if (!admin && task.assignee_id !== meId) return;
    const next = task.status === "done" ? "open" : "done";
    setTasks((rows) => rows.map((row) => (row.id === task.id ? { ...row, status: next } : row)));
    try {
      const saved = await apiJson(`/api/tasks/${task.id}`, { status: next }, "PATCH");
      if (saved?.id) setTasks((rows) => rows.map((row) => (row.id === task.id ? { ...row, ...saved } : row)));
    } catch {
      setTasks((rows) => rows.map((row) => (row.id === task.id ? task : row)));
    }
  }

  async function createTask(title, assigneeId) {
    const draft = { id: `local_${Date.now()}`, title, assignee_id: assigneeId, status: "open" };
    setTasks((rows) => [...rows, draft]);
    try {
      const saved = await apiJson("/api/tasks", { title, assignee_id: assigneeId, status: "open" });
      if (saved?.id) setTasks((rows) => rows.map((row) => (row.id === draft.id ? { ...draft, ...saved } : row)));
    } catch {
      setTasks((rows) => rows.filter((row) => row.id !== draft.id));
    }
  }

  async function deleteTask(task) {
    if (!admin) return;
    if (!window.confirm(w.confirmDelete)) return;
    setTasks((rows) => rows.filter((row) => row.id !== task.id));
    try {
      await api(`/api/tasks/${task.id}`, { method: "DELETE" });
    } catch {
      setTasks((rows) => [...rows, task]);
    }
  }

  async function renameTask(task, title) {
    const next = String(title || "").trim();
    if (!next) return;
    setTasks((rows) => rows.map((row) => (row.id === task.id ? { ...row, title: next } : row)));
    try {
      const saved = await apiJson(`/api/tasks/${task.id}`, { title: next }, "PATCH");
      if (saved?.id) setTasks((rows) => rows.map((row) => (row.id === task.id ? { ...row, ...saved } : row)));
    } catch {
      setTasks((rows) => rows.map((row) => (row.id === task.id ? task : row)));
    }
  }

  async function restoreTask(task) {
    try {
      const saved = await apiJson(`/api/tasks/${task.id}/restore`, {}, "POST");
      if (saved?.id) setTasks((rows) => [...rows.filter((row) => row.id !== saved.id), saved]);
    } catch {
      /* la corbeille reste */
    }
  }

  async function assignTask(task, assigneeId) {
    setTasks((rows) => rows.map((row) => (row.id === task.id ? { ...row, assignee_id: assigneeId } : row)));
    try {
      const saved = await apiJson(`/api/tasks/${task.id}`, { assignee_id: assigneeId }, "PATCH");
      if (saved?.id) setTasks((rows) => rows.map((row) => (row.id === task.id ? { ...row, ...saved } : row)));
    } catch {
      setTasks((rows) => rows.map((row) => (row.id === task.id ? task : row)));
    }
  }

  const focused = hunters.find((hunter) => hunter.id === modal);
  const myHunter = hunters.find((hunter) => hunter.id === meId);
  const mineOpen = myHunter ? openCount(tasks, myHunter.id) : 0;

  function close() {
    if (layerRef.current) {
      setLayer(null);
      return;
    }
    setModal(null);
    setLayer(null);
    setPdfOpen(false);
  }

  return (
    <main className={`og-cockpit23 og-hunt38-page${isMarcel && mode === "admin" ? " is-admin" : ""}`} data-testid="page-today">
      {isMarcel && mode === "admin" ? (
        <AdminCockpit onOpen={setModal} planDay={activeDay} />
      ) : (
        <HunterCockpit
          now={now}
          onOpen={setModal}
          hunters={hunters}
          tasks={tasks.filter(isLiveTask)}
          hunterLabel={hunterLabel}
          t={t}
          planDay={activeDay}
        />
      )}

      {modal ? (
        <PWSWorkModal title={modalTitle(modal, t, focused, activeDay)} onClose={close} testId={`modal-${modalKey(modal)}`} size={modal === "group-map" ? "map" : ""} skin={modalSkin(modal)} subtitle={modal === "group-map" ? "Nos repères. Notre territoire." : ""}>
          {focused ? (
            <div className="og-person-sheet" data-testid="person-sheet">
              <h3>À faire et fait</h3>
              <TaskBoard
                tasks={tasks.filter(isLiveTask)}
                memberId={focused.id}
                hunters={hunters}
                admin={admin}
                meId={meId}
                unknown={unknown}
                work={w}
                onToggle={toggleTask}
                onCreate={createTask}
                onAssign={assignTask}
                onDelete={deleteTask}
                onRename={renameTask}
                onRestore={restoreTask}
              />
              <PersonPack memberId={focused.id} onNordQuest={() => setModal("nordquest")} />
            </div>
          ) : null}
          {modal === "mine" ? (
            <GangDesk tasks={tasks} hunters={hunters} meId={meId} admin={admin} onlyMine onToggle={toggleTask} onCreate={createTask} onAssign={assignTask} onDelete={deleteTask} onRename={renameTask} />
          ) : null}
          {modal === "gang" ? (
            <GangDesk tasks={tasks} hunters={hunters} meId={meId} admin={admin} onToggle={toggleTask} onCreate={createTask} onAssign={assignTask} onDelete={deleteTask} onRename={renameTask} />
          ) : null}
          {modal === "meals" ? <MealsPanel admin={admin} /> : null}
          {modal === "humour" ? <HumourPanel work={w} admin={admin} /> : null}
          {modal === "photos-prep" ? <PrepPhotoPanel work={w} /> : null}
          {modal === "nordquest" ? <NordQuestCenter /> : null}
          {modal === "gear" ? <><DutyList /><GearFacts meId={meId} admin={admin} /></> : null}
          {modal === "ready" ? <><ReadyStrip tasks={tasks.filter(isLiveTask)} /><PermitReminder /><Readiness tasks={tasks.filter(isLiveTask)} /><OfficialSources /></> : null}
          {modal === "territory-read" ? <TerritoryGuide /> : null}
          {modal === "harvest" ? <HarvestPanel /> : null}
          {modal === "return" ? <ReturnPanel onOpen={setModal} day={activeDay} /> : null}
          {modal === "observation" ? <ObservationForm day={activeDay} /> : null}
          {modal === "close-day" ? <CloseDay day={activeDay} weather={weatherSnap} /> : null}
          {modal === "vehicles" ? <VehicleFacts /> : null}
          {modal === "chalet" ? <RoomsPanel /> : null}
          {modal === "plan" || modal === "organize" ? <DayTeams day={activeDay} admin={admin} hunters={hunters} onWeather={() => setModal("weather")} /> : null}
          {modal === "route" ? <RouteModal suggested={defaultDestination(now)} margin={nextStep(now).countdown} /> : null}
          {modal === "today" ? (
            <>
              <WeatherCard />
              <p>{LUNCH}</p>
              <button type="button" className="og-tile" onClick={() => setModal("meals")}>Mon lunch</button>
              <button type="button" className="og-tile" onClick={() => setModal("organize")}>Plan du jour</button>
            </>
          ) : null}
          {modal === "prospection" ? (
            <div className="og-fit__actions">
              <button type="button" className="og-tile" onClick={() => setModal("photos")}>Photos</button>
              <button type="button" className="og-tile" onClick={() => setModal("map")}>Carte</button>
              <button type="button" className="og-tile" onClick={() => setModal("story")}>Histoire</button>
              <button type="button" className="og-tile" onClick={() => setModal("bilan")}>Bilan</button>
            </div>
          ) : null}
          {modal === "after" ? (
            <>
              <p>{t.afterHuntTitle}</p>
              <p>{t.bilanWhen}</p>
              <button type="button" className="og-tile" onClick={() => setModal("humour")}>{w.humour}</button>
            </>
          ) : null}
          {modal === "weather" ? <HuntConditions day={activeDay} /> : null}
          {modal === "org" || modal === "schedule" ? <><SepaqInscription /><OrganizationPanel onOpenPdf={() => setPdfOpen(true)} /></> : null}
          {modal === "group-map" ? <GroupBoard /> : null}
          {modal === "day" ? (
            <DaySheet
              day={activeDay}
              anchor={torontoIso(now)}
              onSelect={setSheetDay}
              onOpen={setLayer}
              tasks={tasks.filter(isLiveTask)}
              myHunter={myHunter}
              hunters={hunters}
            />
          ) : null}
          {modal === "nordquest-drop" ? <GroupMap mode="return" /> : null}
          {modal === "nordquest-versions" ? <GroupMap mode="versions" /> : null}
          {modal === "group" ? (
            <ul className="og-meal-list">
              {hunters.map((hunter) => <li key={hunter.id}><strong>{hunter.first_name}</strong><span>{hunterLabel(hunter)}</span></li>)}
            </ul>
          ) : null}
          {modal === "notes" ? <NotesPanel admin={admin} unknown={unknown} /> : null}
          {modal === "expenses" ? <ExpensesPage scope="hunt" /> : null}
          {modal === "photos" ? <PhotoGallery fallback={hunt.photos || []} /> : null}
          {modal === "map" || modal === "territory" ? (
            <div className="og-modal-map" data-testid="modal-map">
              <p>{terr.label || t.place}</p>
              <p>{t.fieldMap}</p>
              <p data-historical="prospect">{t.toolHistory}</p>
              <p>{w.askMarcel}</p>
              <Suspense fallback={<p className="og-hint">{ui.app.loading}</p>}>
                <TerritoryMap points={mapBits?.points || []} limit={mapBits?.limit} full={false} testId="modal-territory-map" />
              </Suspense>
            </div>
          ) : null}
          {modal === "story" ? (
            <div>
              <p>{prospect.dates} · {t.completed}</p>
              <p>{t.sevenPresent}</p>
              <ul>{present.map((name) => <li key={name}>{name}</li>)}</ul>
              <p data-historical="prospect">{t.toolHistory}</p>
            </div>
          ) : null}
          {modal === "bilan" ? <p>{bilan.label}</p> : null}
        </PWSWorkModal>
      ) : null}
      {layer ? (
        <PWSWorkModal title={modalTitle(layer, t, null, activeDay)} onClose={() => setLayer(null)} testId={`modal-${modalKey(layer)}`} stacked closeTestId="work-modal-close-layer">
          {layer === "territory-read" ? <TerritoryGuide /> : null}
          {layer === "observation" ? <ObservationForm day={activeDay} /> : null}
          {layer === "return" ? <ReturnPanel onOpen={setLayer} day={activeDay} /> : null}
          {layer === "harvest" ? <HarvestPanel /> : null}
          {layer === "nordquest-drop" ? <GroupMap mode="return" /> : null}
          {layer === "nordquest" ? <NordQuestCenter /> : null}
          {layer === "organize" || layer === "plan" ? <DayTeams day={activeDay} admin={admin} hunters={hunters} onWeather={() => setLayer("weather")} /> : null}
          {layer === "weather" ? <HuntConditions day={activeDay} /> : null}
          {layer === "gear" ? <><DutyList /><GearFacts meId={meId} admin={admin} /></> : null}
          {layer === "expenses" ? <ExpensesPage scope="hunt" /> : null}
        </PWSWorkModal>
      ) : null}
      {pdfOpen ? (
        <PWSWorkModal title={w.pdfTitle} onClose={() => setPdfOpen(false)} testId="modal-sepaq-pdf">
          <div className="og-pdf-view" data-testid="sepaq-pdf-view">
            <p>{w.pdfPreview}</p>
            <img src="/docs/por_fiche_technique_or_2026-p1.jpg" alt={w.pdfTitle} data-testid="sepaq-pdf-preview" />
            <img src="/docs/por_fiche_technique_or_2026-p2.jpg" alt="" data-testid="sepaq-pdf-preview-2" />
            <a className="og-btn og-btn--primary" href={SEPAQ_PDF} download data-testid="sepaq-pdf-download">{w.downloadPdf}</a>
            <a className="og-btn" href={SEPAQ_PDF} target="_blank" rel="noopener noreferrer" data-testid="sepaq-pdf-open-tab">{w.openPdf}</a>
          </div>
        </PWSWorkModal>
      ) : null}
    </main>
  );
}

function modalSkin(modal) {
  if (modal === "weather") return "weather";
  if (modal === "gang" || modal === "mine") return "gang";
  if (modal === "group-map") return "map";
  if (modal === "route") return "route";
  if (modal === "nordquest" || modal === "nordquest-drop") return "nordquest";
  if (modal === "meals") return "meals";
  if (modal === "gear" || modal === "vehicles") return "gear";
  return "forest";
}

function modalKey(modal) {
  return HUNTER_ORDER.includes(modal) ? "person" : modal;
}

function modalTitle(modal, t, hunter, day) {
  if (hunter) return hunter.first_name;
  if (modal === "plan" || modal === "organize") return `Organiser — ${dayLabel(day)}`;
  const w = t.work;
  const titles = {
    mine: w.myTasks,
    gang: w.gangTasks,
    meals: w.meals,
    humour: w.humourTitle,
    "photos-prep": w.photoValidate,
    nordquest: "NordQuest — mon téléphone est-il prêt ?",
    gear: w.gear,
    vehicles: w.vehicles,
    org: w.orgTitle,
    schedule: w.orgTitle,
    territory: w.territory,
    map: w.map,
    group: w.group,
    notes: w.notes,
    expenses: w.expensesTitle,
    chalet: "Chalet",
    today: "Aujourd’hui",
    prospection: "Prospection terminée",
    after: "Après la chasse",
    plan: "Organiser la journée",
    organize: "Organiser la journée",
    route: "Vérifier mon trajet",
    "group-map": "Carte du groupe",
    day: "Voir ma journée",
    "nordquest-drop": "Déposer mes points",
    "nordquest-versions": "Versions du groupe",
    "close-day": "Fermer la journée",
    ready: "Sommes-nous prêts ?",
    "territory-read": "Lire le territoire",
    harvest: "Orignal abattu",
    return: "Retour au chalet",
    observation: "Observation",
    weather: "Conditions de chasse",
    photos: w.photos,
    story: w.story,
    bilan: w.bilan,
  };
  return titles[modal] || t.title;
}
